import React, { useState, useEffect, useMemo } from "react";

/**
 * usePager — client-side pagination state + slicing.
 *
 *   const { pageRows, page, setPage, pageSize, setPageSize, totalPages, total } = usePager(rows, { defaultSize: 50 });
 *
 * Pass the FULL filtered array (after search/filter) as `rows`. Resets to page 1
 * automatically when the row count changes (e.g. filters narrow the list).
 */
export function usePager(rows, { defaultSize = 50, sizes = [25, 50, 100] } = {}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultSize);

  // Reset to page 1 whenever the underlying set or page size changes.
  useEffect(() => { setPage(1); }, [rows?.length, pageSize]);

  const total = rows?.length || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => (rows || []).slice((safePage - 1) * pageSize, safePage * pageSize),
    [rows, safePage, pageSize]
  );

  return {
    page: safePage,
    setPage,
    pageSize,
    setPageSize,
    sizes,
    total,
    totalPages,
    pageRows,
  };
}

/**
 * <Pager /> — bottom-of-table controls: "showing X-Y of Z", page size dropdown,
 * Prev/Next buttons. Pair with usePager.
 */
export function Pager({ page, setPage, pageSize, setPageSize, sizes, total, totalPages, testidPrefix = "pager" }) {
  if (total === 0) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return (
    <div className="px-4 py-3 border-t border-zinc-200 flex flex-col sm:flex-row gap-2 items-start sm:items-center justify-between text-xs">
      <div className="text-zinc-500 font-mono" data-testid={`${testidPrefix}-summary`}>
        showing <span className="text-zinc-800">{from}</span>–<span className="text-zinc-800">{to}</span> of <span className="text-zinc-800">{total}</span>
      </div>
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-1.5 text-zinc-500 uppercase tracking-wider font-mono text-[10px]">
          rows
          <select
            data-testid={`${testidPrefix}-size`}
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="border border-zinc-200 rounded-md px-2 py-1 text-xs bg-white"
          >
            {sizes.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <div className="flex items-center gap-1">
          <button data-testid={`${testidPrefix}-prev`} disabled={page <= 1} onClick={() => setPage(page - 1)}
            className="border border-zinc-200 rounded-md px-2.5 py-1 disabled:opacity-40 hover:bg-zinc-50">Prev</button>
          <span className="font-mono px-2 text-zinc-500">{page} / {totalPages}</span>
          <button data-testid={`${testidPrefix}-next`} disabled={page >= totalPages} onClick={() => setPage(page + 1)}
            className="border border-zinc-200 rounded-md px-2.5 py-1 disabled:opacity-40 hover:bg-zinc-50">Next</button>
        </div>
      </div>
    </div>
  );
}
